<template>
  <div class="tree-data-table">
    <v-card>
      <v-card-title>
        <AdButton type="expand" list medium @click="expandAll" darker/>
        <AdButton type="collapse" list medium @click="collapseAll" darker/>

        <slot name="buttons"></slot>

        <v-spacer></v-spacer>

        <v-text-field
          v-model="search"
          class="mt-2 mr-2 xmax-300"
          append-icon="mdi-magnify"
          :label="$t('search')"
          single-line
          hide-details
          solo
          dense
          clearable
         ></v-text-field>

      </v-card-title>
      <v-card-text>

        <slot name="tableactions"></slot>

        <v-data-table
          :headers="headers"
          :items="items"
          :search="search"
          :loading="loading"
          :custom-filter="onSearch"
          disable-pagination
          disable-sort>
          <template v-slot:item="{ item }">
            <tr v-show="isItemVisible(item)"
              v-bind:style="'--level: ' + item.__meta.level + ';'"
              :class="item.__class">
              <td v-for="({ value }, key) of headers" :key="key">
                <template v-if="key === 0">
                  <v-btn
                    v-if="item.__meta.isExpandable"
                    v-bind:class="{'v-btn--rotate': item.__meta.isExpanded}"
                    @click="toggleItemExpanded(item)"
                    icon
                  >
                    <v-icon>arrow_right</v-icon>
                  </v-btn>
                </template>
                <slot :name="'item.' + value" v-bind="item">
                    <span v-html="item[value]"></span>
                </slot>
              </td>
            </tr>
          </template>

          <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
            <slot :name="slot" v-bind="scope"/>
          </template>
        </v-data-table>

      </v-card-text>
    </v-card>
  </div>
</template>

<script>
// Export tree view table component.
export default {
  /**
   * Component properties.
   */
  props: {
    headers: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    editmode: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data () {
    return {
      search: '',
      pointer: 0
    }
  },

  /**
   * Component methods.
   */
  methods: {
    /**
     * Check if item is visible.
     *
     * @param item
     * @returns {boolean}
     */
    isItemVisible (item) {
      if (item.absent && !this.editmode) return false

      // Check if item has no parent.
      if (item.__meta.level === 0 || !item.__meta.parent) {
        // Return true because it's the first level.
        return true
      }

      if (this.search.length > 0) {
        // Define search pattern.
        const searchPattern = new RegExp(this.search, 'i')
        if (Object.values(item).join(',').match(searchPattern)) {
          return true
        }
      }

      // Define is expanded.
      let isExpanded = true
      // Define current item.
      let currentItem = item
      // Loop as long current item is not undefined.
      while (currentItem) {
        // Get current item by fetching parent item.
        currentItem = this.items.find(obj => obj.__meta.pointer === currentItem.__meta.parent)
        // Check if current item is not expanded.
        if (currentItem !== undefined && currentItem.__meta.isExpanded === false) {
          // Set is expanded to false.
          isExpanded = false
        }
      }

      // Return true if no table item was found.
      return isExpanded === true
    },

    /**
     * Toggle item expanded.
     *
     * @param item
     */
    toggleItemExpanded (item) {
      item.__meta.isExpanded = item.__meta.isExpanded !== true
    },

    expandAll () {
      // Map all table items.
      this.items.forEach(item => {
        // Check if item has expanded meta key.
        if (item?.__meta?.isExpanded !== undefined) {
          // Set item to expanded.
          item.__meta.isExpanded = true
        }
      })
    },

    collapseAll () {
      // Map all table items.
      this.items.forEach(item => {
        // Check if item has expanded meta key.
        if (item?.__meta?.isExpanded !== undefined) {
          // Set item to not expanded.
          item.__meta.isExpanded = false
        }
      })
    },

    onSearch (value, search, item) {
      if (!value || !search || typeof value !== 'string') {
        return false
      }

      // Define search pattern.
      const searchPattern = new RegExp(this.search, 'i')
      if (Object.values(item).join(',').match(searchPattern)) {
        return true
      }

      // Define current item.
      let currentItem = item
      // Loop as long current item is not undefined.
      while (currentItem) {
        // Get current item by fetching parent item.
        currentItem = this.items.find(obj => obj.__meta.pointer === currentItem.__meta.parent)
        // Check if current item is not expanded.
        if (currentItem !== undefined && Object.values(currentItem).join(',').match(searchPattern)) {
          return true
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
    .tree-data-table {
        table {
            tr {
                td {
                    padding: 11px 0;
                    vertical-align: top;
                }

                td:first-child {
                    padding-left: calc((16px + 36px) + (var(--level) * 22px)) !important;

                    .v-btn {
                        margin-left: -36px;
                    }
                }
            }

            .v-btn {
                width: 24px;
                height: 24px;
                min-width: 24px;
                min-height: 24px;
                margin: 0 5px;

                &.v-btn--rotate {
                    transform: rotateZ(90deg);
                }
            }
        }
    }
    .xmax-100 {
      max-width: 300px;
    }
</style>
